/**
 * Interval utility functions for parsing and validating question intervals
 * Used across Reading and Listening forms
 */

/**
 * Parse an interval string (e.g., "27-40") and return {start, end}
 * @param {string} intervalStr - The interval string to parse
 * @returns {{start: number, end: number} | null} - Parsed interval or null if invalid
 */
export const parseInterval = (intervalStr) => {
  if (!intervalStr || !intervalStr.includes('-')) return null;
  const parts = intervalStr.split('-');
  if (parts.length < 2) return null;
  const start = parseInt(parts[0].trim(), 10);
  const end = parseInt(parts[1].trim(), 10);
  if (isNaN(start) || isNaN(end) || end < start) return null;
  return { start, end };
};

/**
 * Calculate question count from an interval string
 * @param {string} interval - The interval string (e.g., "1-13")
 * @returns {number} - Number of questions in the interval
 */
export const parseIntervalToCount = (interval) => {
  const parsed = parseInterval(interval);
  return parsed ? parsed.end - parsed.start + 1 : 0;
};

/**
 * Validate that intervals are consecutive and non-overlapping
 * @param {string[]} intervals - Array of interval strings
 * @param {string} sectionLabel - Label for sections (e.g., "Passage" or "Part")
 * @returns {string} - Error message or empty string if valid
 */
export const validateIntervalSequence = (intervals, sectionLabel = 'Section') => {
  const parsed = intervals.map(parseInterval).filter(Boolean);

  for (let i = 1; i < parsed.length; i++) {
    const prev = parsed[i - 1];
    const curr = parsed[i];
    if (curr && prev && curr.start !== prev.end + 1) {
      return `${sectionLabel} ${i + 1} interval should start at ${prev.end + 1}.`;
    }
  }

  return '';
};

/**
 * Calculate total question count from multiple intervals
 * @param {Object} sections - Object with section data containing totalQuestionCount
 * @param {string[]} keys - Keys to check in sections object
 * @returns {number} - Total question count
 */
export const calculateTotalQuestionCount = (sections, keys) => {
  return keys.reduce((total, key) => {
    return total + Number(sections[key]?.totalQuestionCount || 0);
  }, 0);
};

/**
 * Validate total question count doesn't exceed maximum
 * @param {number} totalCount - The total count to validate
 * @param {number} maxCount - Maximum allowed count (default 40)
 * @returns {string} - Error message or empty string if valid
 */
export const validateTotalQuestionCount = (totalCount, maxCount = 40) => {
  if (totalCount > maxCount) {
    return `Total question count cannot exceed ${maxCount}. Current total is ${totalCount}.`;
  }
  return '';
};
